import React from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { userIdAtom, userEmailCheckAtom } from '../store/company';

export const pivotLabels = {
    'Cost_Type_Code': 'Cost Type',
    'Discipline': 'Discipline',
    'Resource_Code': 'Resource',
    'Phase_Code': 'Phase',
    'Supplier_Name': 'Vendor Name',
    'PO_Number': 'PO Number',
    'Line_ID': 'Line',
    'Equipment_Tier': 'Equipment',
};

const shortLabels = {
    'Cost_Type_Code': 'Cost Type',
    'Discipline': 'Discipline',
    'Resource_Code': 'Resource',
    'Phase_Code': 'Phase',
    'Supplier_Name': 'Vendor',
    'PO_Number': 'PO',
    'Line_ID': 'Line',
    'Equipment_Tier': 'Equip',
};

const presets = [
    { label: 'Vendor', tooltip: 'Vendor → PO → Line', path: ['Supplier_Name', 'PO_Number', 'Line_ID'] },
    { label: 'Resource', tooltip: 'Resource → Vendor → PO → Line', path: ['Resource_Code', 'Supplier_Name', 'PO_Number', 'Line_ID'] },
    { label: 'Discipline', tooltip: 'Discipline → Phase → Vendor → PO → Line', path: ['Discipline', 'Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID'] },
    { label: 'Cost Type', tooltip: 'Cost Type → Vendor → PO → Line', path: ['Cost_Type_Code', 'Supplier_Name', 'PO_Number', 'Line_ID'] },
    { label: 'PO', tooltip: 'PO → Line', path: ['PO_Number', 'Line_ID'] },
    { label: 'Phase', tooltip: 'Phase → Vendor → PO → Line', path: ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID'] },
];

const PivotSelector = ({ columns, available, onChange, costTypes, activeCostTypes, onCostTypeToggle, pageLabel }) => {
    const [userId] = useAtom(userIdAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const canEditColumns = process.env.NODE_ENV !== 'production' || userId === '154045' || userEmailCheck === 'steve.horman@strikeusa.com' || userEmailCheck === 'steven.horman@strikeusa.com';
    const allCodes = costTypes ? Object.keys(costTypes) : [];
    const anyActive = allCodes.some(c => activeCostTypes?.[c]);

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '4px 10px', backgroundColor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <Text style={{ fontSize: 12, fontWeight: '700', color: '#475569', letterSpacing: 0.3 }}>Drill Path:</Text>
            {presets.map(p => {
                const isActive = JSON.stringify(columns) === JSON.stringify(p.path.filter(c => available.includes(c)));
                return (
                    <Pressable key={p.label} title={p.tooltip}
                        onPress={() => onChange(p.path.filter(c => available.includes(c)))}
                        style={{ cursor: 'pointer', backgroundColor: isActive ? '#1d4ed8' : '#f1f5f9', borderRadius: 4, padding: '3px 7px', border: isActive ? '1px solid #1d4ed8' : '1px solid #cbd5e1', transition: 'all 0.15s ease' }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: isActive ? '#fff' : '#475569' }}>{p.label}</Text>
                    </Pressable>
                );
            })}
            {canEditColumns && available.filter(c => !columns.includes(c)).length > 0 && (
                <Pressable onPress={() => {
                    const unused = available.filter(c => !columns.includes(c));
                    onChange([unused[0], ...columns]);
                }} style={{ cursor: 'pointer', backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: 4, border: '1px solid #bfdbfe' }}>
                    <Text style={{ fontSize: 12, color: '#2563eb', fontWeight: '600' }}>+ Level</Text>
                </Pressable>
            )}
            {canEditColumns && columns.map((col, i) => (
                <View key={col + i} style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {i > 0 && <Text style={{ fontSize: 13, color: '#94a3b8', marginHorizontal: 3 }}>→</Text>}
                    <select
                        value={col}
                        onChange={(e) => {
                            const next = [...columns];
                            next[i] = e.target.value;
                            onChange(next);
                        }}
                        style={{
                            fontSize: 12,
                            padding: '4px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: 4,
                            backgroundColor: '#fff',
                            color: '#1e293b',
                            cursor: 'pointer',
                            fontWeight: '500',
                        }}
                    >
                        {available.map(a => (
                            <option key={a} value={a} disabled={columns.includes(a) && a !== col}>
                                {shortLabels[a] || a}
                            </option>
                        ))}
                    </select>
                    {columns.length > 1 && (
                        <Pressable onPress={() => onChange(columns.filter((_, j) => j !== i))} style={{ marginLeft: 3, cursor: 'pointer' }}>
                            <Text style={{ fontSize: 14, color: '#94a3b8', fontWeight: 'bold' }}>×</Text>
                        </Pressable>
                    )}
                </View>
            ))}
            {costTypes && onCostTypeToggle && pageLabel && (
                <>
                    <View style={{ width: 1, height: 20, backgroundColor: '#cbd5e1', marginHorizontal: 4 }} />
                    {anyActive ? (
                        <View style={{
                            flexDirection: 'row', alignItems: 'center', gap: 4,
                            backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: '3px 8px',
                        }}>
                            <Text style={{ fontSize: 11, color: '#166534', fontWeight: '500' }}>{pageLabel}</Text>
                            <Pressable onPress={() => allCodes.forEach(c => activeCostTypes?.[c] && onCostTypeToggle(c))} style={{ cursor: 'pointer', marginLeft: 2 }}>
                                <Text style={{ fontSize: 13, color: '#86efac', fontWeight: 'bold' }}>×</Text>
                            </Pressable>
                        </View>
                    ) : (
                        <Pressable
                            onPress={() => allCodes.forEach(c => !activeCostTypes?.[c] && onCostTypeToggle(c))}
                            style={{ cursor: 'pointer', backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: 4, border: '1px solid #bfdbfe' }}
                        >
                            <Text style={{ fontSize: 12, color: '#2563eb', fontWeight: '600' }}>+ {pageLabel}</Text>
                        </Pressable>
                    )}
                </>
            )}
        </View>
    );
};

export default PivotSelector;
